import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useWeb } from '../context/WebContext';
import { 
  FaThLarge, 
  FaList, 
  FaImages, 
  FaCommentAlt, 
  FaMapMarkerAlt, 
  FaEnvelope, 
  FaCog, 
  FaSignOutAlt, 
  FaRedo,
  FaHome,
  FaFileAlt
} from 'react-icons/fa';
import toast, { Toaster } from 'react-hot-toast';

export default function AdminLayout({ children }) {
  const { enquiries, resetAllData } = useWeb();
  const location = useLocation();
  const navigate = useNavigate();

  const unreadEnquiriesCount = enquiries.filter(e => e.status === 'unread').length;

  const menuItems = [
    { name: 'Overview', path: '/admin/dashboard', icon: FaThLarge },
    { name: 'Manage Services', path: '/admin/services', icon: FaList },
    { name: 'Manage Gallery', path: '/admin/gallery', icon: FaImages },
    { name: 'Manage Testimonials', path: '/admin/testimonials', icon: FaCommentAlt },
    { name: 'Manage Locations', path: '/admin/locations', icon: FaMapMarkerAlt },
    { 
      name: 'Customer Enquiries', 
      path: '/admin/enquiries', 
      icon: FaEnvelope, 
      badge: unreadEnquiriesCount > 0 ? unreadEnquiriesCount : null 
    },
    { name: 'Settings & SEO', path: '/admin/settings', icon: FaCog },
  ];

  const handleReset = () => {
    resetAllData();
    toast.success('Website content reset to system defaults!');
    navigate('/admin/dashboard');
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const adminEmail = localStorage.getItem('admin_email') || 'Admin';

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col font-sans">
      <Toaster position="top-right" />
      
      {/* Top Admin Bar */}
      <header className="bg-slate-900 text-white h-16 flex items-center justify-between px-6 z-20 sticky top-0 shadow-md">
        <div className="flex items-center space-x-3">
          <span className="font-extrabold tracking-wider text-orange-500">VENGAMAMBA</span>
          <span className="text-xs uppercase bg-slate-800 text-slate-300 font-bold px-2 py-0.5 rounded">Admin Engine</span>
        </div>
        <div className="flex items-center space-x-4">
          <Link 
            to="/" 
            className="flex items-center space-x-1 text-sm bg-orange-600 text-white hover:bg-orange-700 font-medium px-4 py-1.5 rounded-lg transition-colors"
          >
            <FaHome />
            <span>Go to Live Site</span>
          </Link>
        </div>
      </header>

      {/* Main Admin Workspace */}
      <div className="flex flex-1 flex-col md:flex-row">
        {/* Sidebar */}
        <aside className="w-full md:w-64 bg-slate-850 text-slate-300 flex flex-col border-r border-slate-800 bg-slate-900">
          <nav className="flex-1 px-4 py-6 space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm transition-all ${
                    isActive 
                      ? 'bg-orange-600 text-white font-semibold shadow-md' 
                      : 'hover:bg-slate-800 hover:text-white text-slate-400'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <Icon className="text-base" />
                    <span>{item.name}</span>
                  </div>
                  {item.badge && (
                    <span className="bg-red-500 text-white text-[10px] font-extrabold px-2 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
          
          <div className="p-4 border-t border-slate-800 space-y-1">
            <div className="px-4 py-2">
              <p className="text-xs text-slate-500 uppercase tracking-wider font-bold">Signed in as</p>
              <p className="text-xs text-slate-300 truncate mt-0.5">{adminEmail}</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 w-full text-slate-500 hover:text-red-400 px-4 py-2 text-sm rounded-lg transition-all"
            >
              <FaSignOutAlt />
              <span>Sign Out</span>
            </button>
          </div>
        </aside>

        {/* Content Area */}
        <main className="flex-1 p-6 md:p-10 overflow-y-auto max-w-7xl mx-auto w-full">
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 md:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
